// Copyright (c) MobiFactory. All rights reserved.
// This source code is the property of MobiFactory. It is exclusively available to customers who have a valid user license.
// See LICENSE.txt file in the project root for full license information.

import { PermissionAction, PermissionItem, PermissionScope } from "../model/Permissions";
import { AccountType } from "../model/AccountType";
import _ from "lodash";
import permissionsApi from "../api/permission/permissions";
import oidcManager from "../authentication/oidcManager";

let userPermissions: any | null;

/**
 * Helper class for user permissions
 */
export default class PermissionHelper {
  private userPermissions: any;

  static async initialize() {
    // Get permissions
    const result = await permissionsApi.getUserPermissions();
    if (!result.isSuccess) {
      return;
    }

    userPermissions = result.data;
  }

  /**
   * Checks whether the user has the permission
   * @param {object} permissionContext - the context to check
   * @returns {boolean} true if the user has the specified permission
   */
  static hasPermission(permissionContext: { item: PermissionItem; action: PermissionAction; userId: string | null }) {
    const actionId = permissionContext.action || PermissionAction.READ;

    const userPermission = _.find(
      userPermissions,
      (p) => p.item === permissionContext.item && (p.action & actionId) === actionId
    );

    if (!userPermission) {
      return false;
    }

    // Check permission scope (for update/delete action)
    if (
      userPermission.scope === PermissionScope.RESTRICTED &&
      (actionId === PermissionAction.UPDATE || actionId === PermissionAction.DELETE)
    ) {
      return oidcManager.userProfile.sub === permissionContext.userId;
    }

    return true;
  }

  /**
   * Indicates if the current user has a permission for an account type.
   * @param {AccountType} type - Account type.
   * @param {PermissionAction} action - Action.
   * @returns {boolean} true if the user has permission, false otherwise.
   */
  static hasAccountPermission(type: AccountType, action: PermissionAction) {
    let itemId;

    switch (type) {
      case AccountType.CUSTOMER:
        itemId = PermissionItem.ACCOUNT_CUSTOMER;
        break;

      case AccountType.PROSPECT:
        itemId = PermissionItem.ACCOUNT_PROSPECT;
        break;

      case AccountType.OTHER:
        itemId = PermissionItem.ACCOUNT_OTHER;
        break;

      default:
        return false;
    }

    return this.hasPermission({ item: itemId, action: action, userId: null });
  }
}
